import { set } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import type Component from '@glimmer/component';

interface FixedScrollHeaderParams {
  scrollTopTrigger: number;
}

export interface FixedScrollHeader {
  showHeader: boolean;
  willDestroy: (...args: Parameters<Component['willDestroy']>) => void;
}

export function fixedScrollHeader({
  scrollTopTrigger,
}: FixedScrollHeaderParams) {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  return function <T extends { new (...args: any[]): FixedScrollHeader }>(
    Class: T,
  ) {
    class DecoratedClass extends Class {
      contentContainer: Element | null = null;
      didScroll = false;
      privateScrollHandlerWithInterval: (() => void) | null = null;
      scrollInterval: NodeJS.Timeout | null = null;
      showHeader = false;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      constructor(...args: any[]) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        super(...args);

        // eslint-disable-next-line ember/no-incorrect-calls-with-inline-anonymous-functions, ember/no-runloop
        scheduleOnce('afterRender', this, function () {
          const contentContainer = document.querySelector(
            '#main-content-wrapper-target',
          );

          this.contentContainer = contentContainer;
          this.privateScrollHandlerWithInterval =
            this.scrollHandlerWithInterval.bind(this);

          // Attach the (namespaced) scroll handler on the window
          contentContainer?.addEventListener(
            'scroll',
            this.privateScrollHandlerWithInterval,
          );
        });

        // Launch setInterval that checks flag to detect scrolling every 250ms
        set(
          this,
          'scrollInterval',
          setInterval(() => {
            if (this.didScroll) {
              this.hasScrolled();
            }
          }, 250),
        );
      }

      // @ts-expect-error: Class 'FixedScrollHeader' defines instance member property 'willDestroy', but extended class 'DecoratedClass' defines it as instance member function.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      willDestroy(...args: Parameters<Component['willDestroy']>) {
        super.willDestroy(...args);

        // Cleanup: remove all scroll handlers for this namespace when the component is destroyed
        if (this.contentContainer && this.privateScrollHandlerWithInterval) {
          this.contentContainer.removeEventListener(
            'scroll',
            this.privateScrollHandlerWithInterval,
          );
        }

        if (this.scrollInterval) {
          clearInterval(this.scrollInterval);
        }
      }

      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      hasScrolled() {
        // window.pageYOffset: vanilla JS alternative for $(window).scrollTop()
        const { contentContainer } = this;

        if (contentContainer && contentContainer.scrollTop > scrollTopTrigger) {
          set(this, 'showHeader', true);
        } else {
          set(this, 'showHeader', false);
        }
      }

      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      scrollHandlerWithInterval() {
        set(this, 'didScroll', true);
      }
    }

    Object.defineProperty(DecoratedClass, 'name', { value: Class.name });

    return DecoratedClass;
  };
}
